<template>
  <div class="contact">
    <section class="ct-hero">
      <div class="ct-hero-bg">
        <img :src="img('gen/hero-contact.jpg')" alt="联系我们"/>
        <div class="ct-mask"></div>
        <div class="grid-bg"></div>
      </div>
      <div class="page ct-hero-inner">
        <span class="eyebrow reveal" v-reveal>Contact Us</span>
        <h1 class="h1 reveal" v-reveal="{ delay: 80 }">联系我们</h1>
        <p class="lead reveal" v-reveal="{ delay: 160 }">
          无论是新项目咨询、存量设备改造，还是想先聊聊可行性，都欢迎直接联系皓辰。
        </p>
      </div>
    </section>

    <section class="sec">
      <div class="page">
        <div class="ct-layout">
          <!-- 联系方式 -->
          <div class="ct-info">
            <component
              :is="c.href ? 'a' : 'div'"
              v-for="(c, i) in contacts"
              :key="c.label"
              class="ct-card reveal"
              v-reveal="{ delay: i * 90 }"
              :href="c.href || null"
              :target="c.blank ? '_blank' : null"
              :rel="c.blank ? 'noopener noreferrer' : null"
            >
              <div class="ct-icon"><IconSvg :name="c.icon"/></div>
              <div class="ct-body">
                <span class="ct-label">{{ c.label }}</span>
                <span class="ct-value">{{ c.value }}</span>
                <span class="ct-tip">{{ c.tip }}</span>
              </div>
              <img
                v-if="c.qr"
                class="ct-qr"
                :src="c.qr"
                :alt="c.qrAlt"
                width="176"
                height="176"
                loading="lazy"
              />
              <IconSvg v-if="c.href" name="arrow-right" class="ct-arrow"/>
            </component>

            <div class="ct-demo reveal" v-reveal="{ delay: 240 }">
              <span class="ct-demo-icon"><IconSvg name="key"/></span>
              <div>
                <h3>{{ demo.credentialNote }}</h3>
                <p>{{ demo.contactHint }}</p>
              </div>
            </div>

            <div class="ct-promise reveal" v-reveal="{ delay: 300 }">
              <h3>我们的响应承诺</h3>
              <ul>
                <li v-for="(p, i) in promises" :key="i">
                  <IconSvg name="check"/><span>{{ p }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- 需求表单 -->
          <div class="ct-form card reveal" v-reveal="{ delay: 120 }">
            <h2 class="h3">提交需求，工作日 1 天内回复</h2>
            <p class="form-tip">
              填写后点击「发送需求」，会调用你本机邮件客户端把内容发到我们的邮箱；
              也可以直接拨打电话或加微信，更快。
            </p>

            <div class="field">
              <label>称呼 <i>*</i></label>
              <input v-model.trim="form.name" type="text" placeholder="请输入您的称呼"/>
            </div>

            <div class="field">
              <label>联系电话 <i>*</i></label>
              <input v-model.trim="form.phone" type="tel" placeholder="方便我们与您联系"/>
            </div>

            <div class="field">
              <label>公司 / 单位</label>
              <input v-model.trim="form.company" type="text" placeholder="选填"/>
            </div>

            <div class="field">
              <label>电子邮箱 <i class="opt">选填</i></label>
              <input v-model.trim="form.email" type="email" placeholder="方便我们把方案与报价单发给你"/>
            </div>

            <div class="field">
              <label>咨询方向 <i>*</i></label>
              <div class="chips">
                <button
                  v-for="b in directions"
                  :key="b.key"
                  type="button"
                  class="chip"
                  :class="{ on: form.direction === b.key }"
                  @click="form.direction = b.key"
                >{{ b.name }}</button>
              </div>
            </div>

            <div class="field">
              <label>期望上线时间</label>
              <div class="chips">
                <button
                  v-for="t in timelines"
                  :key="t"
                  type="button"
                  class="chip"
                  :class="{ on: form.timeline === t }"
                  @click="form.timeline = t"
                >{{ t }}</button>
              </div>
            </div>

            <div class="field">
              <label>预算区间 <i class="opt">选填</i></label>
              <div class="chips">
                <button
                  v-for="b in budgets"
                  :key="b"
                  type="button"
                  class="chip"
                  :class="{ on: form.budget === b }"
                  @click="form.budget = b"
                >{{ b }}</button>
              </div>
            </div>

            <div class="field">
              <label>需求描述</label>
              <textarea v-model.trim="form.message" rows="4" placeholder="例如：30 亩大棚，想做温湿度监测与自动灌溉"></textarea>
            </div>

            <label class="consent">
              <input v-model="form.consent" type="checkbox"/>
              <span>
                我已阅读并同意
                <router-link to="/privacy" target="_blank">《隐私政策》</router-link>，
                同意网站按该政策处理我提交的信息。
              </span>
            </label>

            <p v-if="error" class="form-error">{{ error }}</p>
            <p v-if="sent" class="form-ok">
              已打开邮件客户端，请点击发送完成提交。若没有反应，请把需求发到
              <b>{{ company.email }}</b>，或直接拨打 {{ company.phone }}（微信同号）。
              我们会在工作日 1 天内响应。
            </p>

            <div class="form-actions">
              <button class="btn btn-primary" type="button" @click="submit">
                发送需求
                <IconSvg name="arrow-right"/>
              </button>
              <button class="btn btn-ghost" type="button" @click="copyContact">
                {{ copied ? '已复制' : '复制联系方式' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="sec-sm">
      <div class="page">
        <div class="faq">
          <h2 class="h3">常见问题</h2>
          <div class="faq-grid">
            <div v-for="(f, i) in faqs" :key="i" class="faq-item reveal" v-reveal="{ delay: i * 80 }">
              <h4>{{ f.q }}</h4>
              <p>{{ f.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <CtaBand
      eyebrow="Call Now"
      :title="`直接电话沟通：${company.phone}`"
      desc="工作时间内电话通常可直接接通，非工作时间请留言，我们会尽快回电。"
      :bg="img('gen/cta-bg.jpg')"
    />
  </div>
</template>

<script>
import IconSvg from '@/components/common/IconSvg'
import CtaBand from '@/components/common/CtaBand'
import img from '@/utils/assets'
import { company, businessLines, demo } from '@/data/site'

export default {
  name: 'ContactPage',
  components: { IconSvg, CtaBand },
  data() {
    return {
      company,
      demo,
      img,
      copied: false,
      sent: false,
      error: '',
      form: {
        name: '',
        phone: '',
        email: '',
        company: '',
        direction: 'agriculture',
        timeline: '尽快',
        budget: '暂不明确',
        message: '',
        consent: false
      },
      directions: businessLines.map((l) => ({ key: l.key, name: l.name })),
      timelines: ['尽快', '1 个月内', '1–3 个月', '再评估'],
      budgets: ['暂不明确', '10 万以内', '10–30 万', '30 万以上'],
      contacts: [
        { icon: 'phone', label: '联系电话', value: company.phone, tip: '工作时间 9:00 - 18:00', href: `tel:${company.phone}` },
        {
          icon: 'users2',
          label: company.wechat.label,
          value: company.wechat.search,
          tip: company.wechat.tip,
          href: null,
          qr: company.wechat.qr ? img(company.wechat.qr) : '',
          qrAlt: `${company.name} 微信二维码`
        },
        { icon: 'mail', label: '电子邮箱', value: company.email, tip: '方案与合作咨询', href: `mailto:${company.email}` },
        {
          icon: 'location',
          label: '公司地址',
          value: company.address,
          tip: '点击在地图查看，来访请提前预约',
          href: `https://www.amap.com/search?query=${encodeURIComponent(company.address)}`,
          blank: true
        },
        { icon: 'play', label: demo.label, value: demo.url, tip: '演示账号密码请联系获取', href: demo.url, blank: true }
      ],
      promises: [
        '工作日 1 天内响应咨询，先判断可行性',
        '免费提供初步技术方案思路与预算区间',
        '涉及现场的项目，先勘查再报价',
        '不夸大功能，不承诺做不到的指标'
      ],
      faqs: [
        {
          q: '可以先做一小部分试点吗？',
          a: '可以。我们通常建议先在一个地块、一条产线或一个部门做小范围试点，验证效果后再推广，降低一次性投入风险。'
        },
        {
          q: '存量设备还能接进来吗？',
          a: '大部分可以。支持 Modbus RTU/TCP、OPC UA、MQTT 等主流协议，老设备可通过加装采集模块或边缘网关接入，通常不需要停机改造。'
        },
        {
          q: '项目大概需要多长时间？',
          a: '监测类项目（数据采集 + 看板告警）一般 2-4 周可上线；含自动化控制的方案视点位数量与现场条件，通常 4-8 周。具体以勘查后的方案为准。'
        },
        {
          q: '系统上线后谁负责维护？',
          a: '皓辰提供运维值守与定期巡检，也可按年签订运维服务，包含故障响应、策略优化和功能迭代。'
        }
      ]
    }
  },
  methods: {
    validate() {
      if (!this.form.name) return '请填写您的称呼'
      if (!/^[\d\-+\s()]{6,20}$/.test(this.form.phone)) return '请填写正确的联系电话'
      if (this.form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) return '邮箱格式看起来不正确，可留空'
      if (!this.form.direction) return '请选择咨询方向'
      if (!this.form.consent) return '请先阅读并勾选《隐私政策》告知，我们才能处理你提交的信息'
      return ''
    },
    submit() {
      this.error = ''
      const err = this.validate()
      if (err) {
        this.error = err
        return
      }
      const dirName = (this.directions.find((d) => d.key === this.form.direction) || {}).name || ''
      const subject = `【官网咨询】${dirName} - ${this.form.name}`
      const body = [
        `咨询方向：${dirName}`,
        `称呼：${this.form.name}`,
        `联系电话：${this.form.phone}`,
        `电子邮箱：${this.form.email || '未填写'}`,
        `公司/单位：${this.form.company || '未填写'}`,
        `期望上线时间：${this.form.timeline || '未选择'}`,
        `预算区间：${this.form.budget || '未选择'}`,
        '',
        '需求描述：',
        this.form.message || '未填写',
        '',
        '—— 来自皓辰科技官网咨询表单（已同意隐私政策告知）'
      ].join('\n')

      this.sent = true
      window.location.href = `mailto:${this.company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    },
    copyContact() {
      const text =
        `${this.company.name}\n电话：${this.company.phone}\n微信：${this.company.wechat.search}` +
        `\n邮箱：${this.company.email}\n地址：${this.company.address}`
      const done = () => {
        this.copied = true
        window.setTimeout(() => { this.copied = false }, 2200)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(done)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy') } catch (e) { /* 忽略：浏览器不支持自动复制 */ }
        document.body.removeChild(ta)
        done()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ct-hero {
  position: relative;
  min-height: 52vh;
  display: flex;
  align-items: center;
  padding: calc(var(--nav-h) + 40px) 0 66px;
  overflow: hidden;
}

.ct-hero-bg {
  position: absolute;
  inset: 0;

  img { width: 100%; height: 100%; object-fit: cover; opacity: 0.42; }

  .ct-mask {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 60% at 30% 45%, rgba(61, 123, 255, 0.2), transparent 70%),
      linear-gradient(180deg, rgba(3, 7, 17, 0.88), rgba(3, 7, 17, 0.74) 52%, rgba(3, 7, 17, 0.98));
  }
}

.ct-hero-inner { position: relative; z-index: 2; }
.ct-hero .h1 { margin-top: 20px; }
.ct-hero .lead { margin-top: 20px; max-width: 680px; }

.ct-layout {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 40px;
  align-items: start;
}

/* 联系卡片 */
.ct-info { display: flex; flex-direction: column; gap: 16px; }

.ct-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 24px 26px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(140deg, rgba(12, 26, 52, 0.72), rgba(5, 12, 27, 0.56));
  transition: transform 0.45s, border-color 0.45s, box-shadow 0.45s;

  &:hover {
    transform: translateX(6px);
    border-color: var(--line-strong);
    box-shadow: -18px 0 44px -30px rgba(34, 224, 255, 0.9);
  }

  &:hover .ct-icon { color: var(--cyan); border-color: var(--cyan); }
  &:hover .ct-arrow { color: var(--cyan); transform: translateX(4px); }
}

.ct-icon {
  width: 46px;
  height: 46px;
  padding: 12px;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid var(--line-strong);
  color: var(--txt-2);
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.4s;
}

/* 微信二维码：白底留白保证可扫，暗色卡片上作为独立视觉块呈现 */
.ct-qr {
  width: 176px;
  height: 176px;
  flex: 0 0 auto;
  margin-left: auto;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 28px -18px rgba(0, 0, 0, 0.9);
}

.ct-body { display: flex; flex-direction: column; gap: 5px; }

/* 移动端：二维码换行到卡片底部，保持足够扫码尺寸 */
@media (max-width: 640px) {
  .ct-card { flex-wrap: wrap; }
  .ct-qr {
    order: 4;
    width: 100%;
    max-width: 216px;
    height: auto;
    margin: 10px 0 0;
  }
}
.ct-label { font-size: 12.5px; letter-spacing: 0.14em; color: var(--txt-3); }
.ct-value { font-size: 16.5px; font-weight: 600; color: #fff; line-height: 1.6; }
.ct-tip { font-size: 12.5px; color: var(--txt-3); }

.ct-arrow {
  position: absolute;
  right: 22px;
  top: 26px;
  width: 18px;
  height: 18px;
  color: var(--txt-3);
  transition: all 0.4s;
}

/* 承诺 */
.ct-demo {
  display: flex;
  gap: 14px;
  padding: 22px;
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--cyan) 42%, transparent);
  background: linear-gradient(120deg, rgba(10, 30, 56, 0.72), rgba(5, 12, 27, 0.6));

  h3 { font-size: 15.5px; color: #fff; }
  p { margin-top: 8px; font-size: 13.5px; line-height: 1.8; color: var(--txt-3); }
}

.ct-demo-icon {
  width: 40px;
  height: 40px;
  padding: 10px;
  flex-shrink: 0;
  border-radius: 12px;
  color: var(--cyan);
  border: 1px solid rgba(34, 224, 255, 0.4);
  background: rgba(34, 224, 255, 0.1);
}

.ct-promise {
  margin-top: 8px;
  padding: 26px 26px 28px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.6), rgba(5, 12, 27, 0.45));

  h3 { font-size: 17px; color: #fff; }

  ul { margin-top: 18px; display: flex; flex-direction: column; gap: 13px; }

  li {
    display: flex;
    gap: 11px;
    font-size: 14px;
    line-height: 1.75;
    color: var(--txt-3);
  }

  svg { width: 17px; height: 17px; flex-shrink: 0; margin-top: 4px; color: var(--green); }
}

/* 表单 */
.ct-form {
  padding: 34px 34px 36px;

  .h3 { color: #fff; }
}

.form-tip { margin-top: 12px; font-size: 13.5px; line-height: 1.8; color: var(--txt-3); }

.field label i.opt { color: var(--txt-3); letter-spacing: 0.06em; }

/* 隐私告知勾选：不勾选不提交（个人信息处理告知） */
.consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 22px;
  font-size: 13.5px;
  line-height: 1.75;
  color: var(--txt-2);
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    margin: 2px 0 0;
    flex-shrink: 0;
    accent-color: var(--cyan);
    cursor: pointer;
  }

  a { color: var(--cyan); }

  a:hover { text-decoration: underline; }
}

.field {
  margin-top: 22px;

  label {
    display: block;
    margin-bottom: 9px;
    font-size: 13.5px;
    color: var(--txt-2);

    i { color: var(--amber); font-style: normal; }
  }

  input, textarea {
    width: 100%;
    padding: 13px 16px;
    font-size: 14.5px;
    color: #fff;
    font-family: inherit;
    border-radius: 12px;
    border: 1px solid var(--line);
    background: rgba(255, 255, 255, 0.035);
    outline: none;
    transition: border-color 0.3s, background 0.3s, box-shadow 0.3s;
    resize: vertical;

    &::placeholder { color: rgba(232, 241, 255, 0.32); }
    &:focus {
      border-color: var(--cyan);
      background: rgba(34, 224, 255, 0.06);
      box-shadow: 0 0 0 3px rgba(34, 224, 255, 0.12);
    }
  }
}

.chips { display: flex; flex-wrap: wrap; gap: 10px; }

.chip {
  padding: 9px 18px;
  font-size: 13.5px;
  color: var(--txt-2);
  border-radius: 999px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.03);
  cursor: pointer;
  transition: all 0.3s;

  &:hover { border-color: var(--line-strong); color: #fff; }
  &.on {
    color: #01060f;
    font-weight: 600;
    border-color: transparent;
    background: var(--grad-main);
  }
}

.form-error { margin-top: 18px; font-size: 13.5px; color: #ff7b7b; }
.form-ok {
  margin-top: 18px;
  padding: 13px 16px;
  font-size: 13.5px;
  line-height: 1.8;
  border-radius: 12px;
  color: #b7ffd9;
  border: 1px solid rgba(36, 227, 154, 0.35);
  background: rgba(36, 227, 154, 0.08);
  b { color: #fff; }
}

.form-actions { display: flex; gap: 14px; margin-top: 28px; flex-wrap: wrap; }

/* FAQ */
.faq {
  .h3 { color: #fff; margin-bottom: 22px; }
}

.faq-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }

.faq-item {
  padding: 24px 26px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: linear-gradient(150deg, rgba(11, 23, 48, 0.62), rgba(5, 12, 27, 0.48));
  transition: transform 0.4s, border-color 0.4s;

  &:hover { transform: translateY(-4px); border-color: var(--line-strong); }

  h4 { font-size: 16px; color: #fff; }
  p { margin-top: 10px; font-size: 14px; line-height: 1.85; color: var(--txt-3); }
}

@media (max-width: 1080px) {
  .ct-layout { grid-template-columns: minmax(0, 1fr); gap: 28px; }
}

@media (max-width: 760px) {
  .ct-hero { min-height: auto; padding: calc(var(--nav-h) + 28px) 0 46px; }
  .ct-form { padding: 26px 20px 28px; }
  .faq-grid { grid-template-columns: minmax(0, 1fr); }
  .form-actions .btn { flex: 1; }
}
</style>
